<template>
  <modal
    @before-open="beforeOpen"
    name="checkout-flow-modal"
    width="440"
    height="auto"
    :adaptive="true"
    :clickToClose="false"
    @opened="blockScroll"
    @closed="onClosed"
  >
    <div
      class="bg-white w-modal max-h-90 p-6 flex flex-col justify-center items-center rounded-lg"
      :dir="arabic ? 'rtl' : 'ltr'"
    >
      <div class="w-full overflow-y-auto">
        <div class="flex justify-between items-start w-full mb-4">
          <p class="text-18 font-semibold w-full" :class="arabic ? 'text-right' : 'text-left'">
            {{ $t("payments.payment_type") }}
          </p>
          <div class="cursor-pointer px-1 py-1" @click="onCloseModal">
            <IconCloseGray />
          </div>
        </div>

        <div v-if="isLoading" class="py-16 flex justify-center items-center w-full">
          <img class="w-20 animate-bounce" src="../../../assets/images/ayadi-loader.svg" alt="Ayadi" />
        </div>

        <p v-if="errorMessage" class="text-ayadiRed font-semibold mb-4" :class="arabic ? 'text-right' : 'text-left'">
          {{ errorMessage }}
        </p>

        <!-- The Flow component renders card / Apple Pay / Google Pay and drives 3DS itself. -->
        <div ref="flowContainer" v-show="!isLoading"></div>

        <div class="flex justify-start items-start mt-5">
          <img src="../../../assets/images/IconShield.svg" :class="arabic ? 'ml-2' : 'mr-2'" alt="shield" />
          <p class="font-semibold text-12 text-lightGray">
            {{ $t("payments.redirected") }}
          </p>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import IconCloseGray from "@/components/icons/IconCloseGray";
import storageClient from "@/services/storageClient";
import { loadCheckoutFlow } from "@/utils/loadCheckoutFlow";
import orderStatusPolling from "@/mixins/orderStatusPolling";
import { isCheckoutFlowIntegrationError } from "@/utils/checkoutFlowError";

// Maps the app's Tailwind palette onto Flow's appearance API so the embedded
// component matches the surrounding modal instead of Checkout's default theme.
// Values are the tokens from tailwind.config.js (ayadi.900, textGray.500,
// formStroke, ayadiRed).
const appearance = {
  colorAction: "#44A4A5",
  colorPrimary: "#44A4A5",
  colorBackground: "#ffffff",
  colorFormBackground: "#ffffff",
  colorBorder: "#E2E1E4",
  colorFormBorder: "#E2E1E4",
  colorSecondary: "#6F7786",
  colorError: "#F04A43",
  colorDisabled: "#E2E1E4",
  colorOutline: "#44A4A5",
  borderRadius: ["4px", "4px"]
};

export default {
  name: "CheckoutFlowModal",
  components: { IconCloseGray },
  mixins: [orderStatusPolling],

  data() {
    return {
      isLoading: true,
      errorMessage: ""
    };
  },

  created() {
    // Deliberately not in data(): none of these drive the template, and Vue 2
    // would deep-observe the SDK's whole object graph on assignment.
    this.orderId = null;
    this.kind = null;
    this.flowComponent = null;
  },

  computed: {
    arabic() {
      return storageClient.getLanguage() === "ar";
    }
  },

  methods: {
    async beforeOpen(value) {
      this.isLoading = true;
      this.errorMessage = "";

      const params = value.params || {};
      this.orderId = params.orderId;
      this.kind = params.kind;

      try {
        // The SDK download needs nothing from the modal, so it runs alongside
        // the render of its own container rather than after it.
        const [CheckoutWebComponents] = await Promise.all([loadCheckoutFlow(), this.$nextTick()]);

        const checkout = await CheckoutWebComponents({
          // Pass the payment_session object through untouched — the SDK reads
          // fields we deliberately do not pick apart.
          paymentSession: params.paymentSession,
          publicKey: params.publicKey,
          environment: params.environment,
          // Flow takes BCP-47 locales; a bare "ar"/"en" is not in its supported list.
          locale: this.arabic ? "ar-AE" : "en-GB",
          appearance,
          onReady: () => {
            this.isLoading = false;
          },
          onPaymentCompleted: () => this.onPaymentCompleted(),
          onError: (_component, error) => this.onFlowError(error)
        });

        this.flowComponent = checkout.create("flow");
        await this.flowComponent.mount(this.$refs.flowContainer);
        this.isLoading = false;
      } catch (e) {
        // Reaching here means the SDK never mounted — a CDN failure or a session
        // the SDK rejected. That is never a decline, so it keeps the "form did
        // not load" wording rather than going through onFlowError().
        // eslint-disable-next-line no-console
        console.error("[checkout-flow] mount failed", e);
        this.isLoading = false;
        this.errorMessage = this.$t("payments.flow_unavailable");
        this.$modal.hide("loader-modal");
      }
    },

    // The SDK callback is UX only — the order is finalized by Checkout's
    // webhook, so we confirm against our own backend before routing.
    async onPaymentCompleted() {
      this.$modal.hide("checkout-flow-modal");
      this.$modal.show("loader-modal");

      const orderId = this.orderId;
      const result =
        this.kind === "package" ? await this.pollPackageOrder(orderId) : await this.pollSessionOrder(orderId);

      this.$modal.hide("loader-modal");

      if (result === "ready") {
        if (this.kind === "package") {
          storageClient.seShowSuccessPackageModal(true);
          this.$modal.show("packages-success-bought-modal");
          return;
        }

        await this.$router.push({ name: "PaymentSuccess", query: { orderId: orderId + "" } }).catch(() => {});
        return;
      }

      if (result === "failed") {
        // PaymentFailedModal, not the inline "payment-failed" in
        // TherapistProfile.vue: that one renders behind `v-if="order"` bound to
        // therapistSlots.order, which this flow never populates — it polls
        // sessions/*. It would open as an empty box. This one takes the order as
        // a param and tolerates a missing failed_reason, which Checkout may not
        // give us.
        this.$modal.show("payment-failed-modal", { order: this.polledOrder() });
        return;
      }

      // timeout — the payment may still settle, so do not claim it failed
      this.$notify({
        group: "errors",
        text: this.$t("payments.status_pending"),
        classes: "toast-error"
      });
    },

    // The order the poll above just refreshed, for whichever flow is running.
    polledOrder() {
      return this.kind === "package" ? this.$store.state.sessions.package_order : this.$store.state.sessions.order;
    },

    // An integration error is our own misconfiguration and says nothing useful
    // to the user, so it is logged and shown as "the form did not load".
    // Everything else is a real refusal and keeps the payment wording.
    onFlowError(error) {
      this.isLoading = false;
      this.$modal.hide("loader-modal");

      if (isCheckoutFlowIntegrationError(error)) {
        // eslint-disable-next-line no-console
        console.error("[checkout-flow] integration error", error);
        this.errorMessage = this.$t("payments.flow_unavailable");
        return;
      }

      this.errorMessage = String(error?.message || "") || this.$t("payments.flow_error");
    },

    onCloseModal() {
      this.$modal.hide("checkout-flow-modal");
      this.$modal.hide("loader-modal");
    },

    blockScroll() {
      document.getElementsByTagName("body")[0].style.overflow = "hidden";
    },

    onClosed() {
      document.getElementsByTagName("body")[0].style.overflow = "auto";
      this.teardownFlow();
    },

    teardownFlow() {
      // Each payment attempt gets a fresh PaymentSession, so the mounted
      // component must not be reused across attempts.
      try {
        this.flowComponent?.unmount();
      } catch (e) {
        // already detached
      }
      this.flowComponent = null;
    }
  }
};
</script>

<style scoped></style>
